import { ReactNode, useRef, useState } from "react";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useTranslation } from "react-i18next";
import { AnalyticsApiError } from "../../../utils/analytics/adminAnalyticsApi";
import { handleAnalyticsError } from "./errorHandling";

const isDev = process.env.NODE_ENV !== "production";

export function AnalyticsQueryProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;

  const [client] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error, query) => {
            if (query.meta?.suppressGlobalError) return;
            if (
              error instanceof AnalyticsApiError &&
              (error.status === 401 || error.status === 503)
            ) {
              return;
            }
            void handleAnalyticsError(error, tRef.current);
          },
        }),
        mutationCache: new MutationCache({
          onError: (error, _vars, _ctx, mutation) => {
            if (mutation.meta?.suppressGlobalError) return;
            void handleAnalyticsError(error, tRef.current);
          },
        }),
        defaultOptions: {
          queries: {
            retry: (failureCount, error) => {
              if (
                error instanceof AnalyticsApiError &&
                [401, 403, 503].includes(error.status)
              ) {
                return false;
              }
              if (error instanceof Error && error.name === "AbortError") {
                return false;
              }
              return failureCount < 1;
            },
            refetchOnWindowFocus: false,
            refetchOnReconnect: true,
          },
        },
      }),
  );

  return (
    <QueryClientProvider client={client}>
      {children}
      {isDev ? <ReactQueryDevtools initialIsOpen={false} /> : null}
    </QueryClientProvider>
  );
}
